import { sopValorMensal } from "@shared/sop-calc";
import type { SalaryTableActions, SalaryTableData } from "@shared/views/salary-table/types";
import { MoreVertical, Plus } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/lib/constants";

const DEFAULT_FX_RATE = 5.3;

type EditableField = "key" | "midValue" | "sopMeio" | "allowedAmplitude";
type EditingCell = { bandId: string; field: EditableField } | null;

function parseCurrencyInput(value: string): number {
  const cleaned = value.replace(/[^\d,.]/g, "");
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  const normalized =
    lastComma > lastDot ? cleaned.replace(/\./g, "").replace(",", ".") : cleaned.replace(/,/g, "");
  const n = parseFloat(normalized);
  return Number.isNaN(n) ? 0 : Math.round(n * 100);
}

export function SalaryTableView({
  data,
  actions,
  isLoading = false,
}: {
  data: SalaryTableData;
  actions?: SalaryTableActions;
  isLoading?: boolean;
}) {
  const fxRate = data.fxRate ?? DEFAULT_FX_RATE;
  const spread = data.spread;
  const canEditBands = Boolean(actions?.onUpdateBandField);
  const canDeleteBands = Boolean(actions?.onDeleteBand);
  const canAddBands = Boolean(actions?.onAddBand);
  const canEditFx = Boolean(actions?.onUpdateFxRate);
  const showActionsColumn = canDeleteBands;

  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [cellValue, setCellValue] = useState("");
  const [editingFxRate, setEditingFxRate] = useState(false);
  const [fxRateInput, setFxRateInput] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isAddPending, setIsAddPending] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);

  const startEdit = (bandId: string, field: EditableField, current: number | string) => {
    if (!canEditBands) return;
    setEditingCell({ bandId, field });
    if (field === "key" || field === "sopMeio" || field === "allowedAmplitude") {
      setCellValue(String(current));
    } else {
      setCellValue(((current as number) / 100).toFixed(2).replace(".", ","));
    }
  };

  const commitEdit = (band: SalaryTableData["bands"][number]) => {
    if (!editingCell || !actions?.onUpdateBandField) {
      setEditingCell(null);
      return;
    }
    const { field } = editingCell;

    if (field === "key" && !cellValue.trim()) {
      setEditingCell(null);
      return;
    }

    let value: number | string;
    if (field === "key") {
      value = cellValue.trim();
    } else if (field === "sopMeio" || field === "allowedAmplitude") {
      const parsed = parseInt(cellValue, 10);
      value = Number.isNaN(parsed) ? 0 : parsed;
    } else {
      value = parseCurrencyInput(cellValue);
    }

    void actions.onUpdateBandField({ bandId: band.id, field, value });
    setEditingCell(null);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, band: SalaryTableData["bands"][number]) => {
    if (e.key === "Enter") commitEdit(band);
    if (e.key === "Escape") setEditingCell(null);
  };

  const submitFxRate = () => {
    if (!actions?.onUpdateFxRate) {
      setEditingFxRate(false);
      return;
    }
    const parsed = parseFloat(fxRateInput.replace(",", "."));
    if (!Number.isNaN(parsed) && parsed > 0) {
      void actions.onUpdateFxRate(parsed);
    }
    setEditingFxRate(false);
  };

  const bands = [...data.bands].sort((a, b) => a.position - b.position);
  const colSpan = showActionsColumn ? 7 : 6;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 max-w-6xl">
        <div className="flex items-center gap-2">
          <Label htmlFor="salary-fx-rate" className="text-sm whitespace-nowrap">
            Taxa de Câmbio (R$/USD)
          </Label>
          {canEditFx && editingFxRate ? (
            <Input
              id="salary-fx-rate"
              value={fxRateInput}
              onChange={(e) => setFxRateInput(e.target.value)}
              onBlur={submitFxRate}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setEditingFxRate(false);
              }}
              className="h-7 w-24"
              autoFocus
              data-testid="input-fx-rate"
            />
          ) : canEditFx ? (
            <button
              type="button"
              className="cursor-pointer hover:text-primary text-sm font-medium bg-transparent border-none p-0"
              data-testid="display-fx-rate"
              onClick={() => {
                setFxRateInput(String(fxRate).replace(".", ","));
                setEditingFxRate(true);
              }}
            >
              {fxRate.toFixed(2).replace(".", ",")}
            </button>
          ) : (
            <span className="text-sm font-medium" data-testid="display-fx-rate">
              {fxRate.toFixed(2).replace(".", ",")}
            </span>
          )}
        </div>
        <span className="text-sm text-muted-foreground">
          Spread: {spread.toFixed(2)} USD
          {data.pricePerShare != null && data.strikePrice != null && (
            <>
              {" "}
              (PPS {data.pricePerShare.toFixed(2)} − Strike {data.strikePrice.toFixed(2)})
            </>
          )}
        </span>
      </div>

      <div className="rounded-md border max-w-6xl">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-48">Nivelamento</TableHead>
              <TableHead>Meio</TableHead>
              <TableHead>SOP 4yr</TableHead>
              <TableHead>SOP R$ mensal</TableHead>
              <TableHead>Total Comp</TableHead>
              <TableHead>Amplitude Permitida</TableHead>
              {showActionsColumn && <TableHead className="w-16">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : bands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                  Nenhuma faixa cadastrada.
                </TableCell>
              </TableRow>
            ) : (
              bands.map((band) => {
                const sopMonthly = sopValorMensal(band.sopMeio, fxRate, spread);
                const totalComp = (band.midValue / 100 + sopMonthly) * 12;
                return (
                  <TableRow key={band.id} data-testid={`row-band-${band.id}`}>
                    {(["key", "midValue", "sopMeio"] as const).map((field) => (
                      <TableCell
                        key={field}
                        data-testid={`cell-band-${band.id}-${field}`}
                        onClick={() => startEdit(band.id, field, band[field])}
                      >
                        {editingCell?.bandId === band.id && editingCell.field === field ? (
                          <Input
                            value={cellValue}
                            onChange={(e) => setCellValue(e.target.value)}
                            onBlur={() => commitEdit(band)}
                            onKeyDown={(e) => handleCellKeyDown(e, band)}
                            className="h-7 w-full"
                            type={field === "sopMeio" ? "number" : "text"}
                            autoFocus
                          />
                        ) : (
                          <span className={canEditBands ? "cursor-pointer hover:text-primary" : ""}>
                            {field === "key"
                              ? band.key
                              : field === "sopMeio"
                                ? band.sopMeio
                                : formatCurrency((band[field] as number) / 100)}
                          </span>
                        )}
                      </TableCell>
                    ))}

                    <TableCell data-testid={`cell-band-${band.id}-sopValorMensal`}>
                      <span>{formatCurrency(sopMonthly)}</span>
                    </TableCell>

                    <TableCell data-testid={`cell-band-${band.id}-totalComp`}>
                      <span>{formatCurrency(totalComp)}</span>
                    </TableCell>

                    <TableCell
                      data-testid={`cell-band-${band.id}-allowedAmplitude`}
                      onClick={() =>
                        startEdit(band.id, "allowedAmplitude", band.allowedAmplitude ?? 0)
                      }
                    >
                      {editingCell?.bandId === band.id &&
                      editingCell.field === "allowedAmplitude" ? (
                        <Input
                          value={cellValue}
                          onChange={(e) => setCellValue(e.target.value)}
                          onBlur={() => commitEdit(band)}
                          onKeyDown={(e) => handleCellKeyDown(e, band)}
                          className="h-7 w-full"
                          type="number"
                          min={0}
                          autoFocus
                        />
                      ) : (
                        <span className={canEditBands ? "cursor-pointer hover:text-primary" : ""}>
                          {band.allowedAmplitude ?? 0}%
                        </span>
                      )}
                    </TableCell>

                    {showActionsColumn && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              data-testid={`button-band-menu-${band.id}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              data-testid={`menuitem-delete-band-${band.id}`}
                              onClick={() => setDeleteTarget(band.id)}
                            >
                              Deletar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {canAddBands && (
        <Button
          variant="ghost"
          data-testid="button-add-band"
          className="mt-3 text-xs font-semibold tracking-wide text-muted-foreground hover:text-foreground uppercase"
          disabled={isAddPending}
          onClick={async () => {
            if (!actions?.onAddBand || isAddPending) return;
            setIsAddPending(true);
            try {
              await actions.onAddBand();
            } finally {
              setIsAddPending(false);
            }
          }}
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar Linha
        </Button>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !isDeletePending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover faixa</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover esta faixa? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletePending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-band"
              disabled={isDeletePending}
              onClick={async (e) => {
                e.preventDefault(); // prevent radix auto-close so we can keep modal open on error
                if (!deleteTarget || !actions?.onDeleteBand || isDeletePending) return;
                setIsDeletePending(true);
                try {
                  await actions.onDeleteBand(deleteTarget);
                  setDeleteTarget(null);
                } finally {
                  setIsDeletePending(false);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
